import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { User } from '../../types';
import { UUID } from '../../types';
import { clearAuthData } from '../../utils/auth';
import { clearTodos } from './todosSlice';
import { clearHabits } from './habitsSlice';
import { clearTrackers } from './trackersSlice';
import { clearNotes } from './notesSlice';
import { clearReminders } from './remindersSlice';
import { API_ENDPOINTS, authenticatedFetch } from '../../config/api';

interface StreakSparkData {
  totalSparkPoints: number;
  currentStreak: number;
  longestStreak: number;
  totalHabitsCompleted: number;
  totalTodosCompleted: number;
  achievements: Achievement[];
  level: number;
  experiencePoints: number;
}

interface Achievement {
  id: UUID;
  name: string;
  description: string;
  icon: string;
  unlockedAt: string; // ISO string
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
}

// Async thunk for manual logout (user initiated)
const manualLogout = createAsyncThunk(
  'user/manualLogout',
  async (_, { dispatch }) => {
    // Clear all slices
    dispatch(clearTodos());
    dispatch(clearHabits());
    dispatch(clearTrackers());
    dispatch(clearNotes());
    dispatch(clearReminders());
    // Clear user data
    dispatch(logout());
    // Redirect to login
    window.location.href = '/login';
  }
);

// Async thunk for fetching user profile
const fetchUserProfile = createAsyncThunk(
  'user/fetchUserProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.AUTH.ME);

      if (!response.ok) {
        throw new Error(`Failed to fetch user profile: ${response.statusText}`);
      }

      const user: User = await response.json();
      return user;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Unknown error'
      );
    }
  }
);

// Async thunk for fetching public user profile
const fetchPublicUserProfile = createAsyncThunk(
  'user/fetchPublicUserProfile',
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await fetch(API_ENDPOINTS.USERS.GET_PUBLIC_PROFILE(userId));

      if (!response.ok) {
        throw new Error(`Failed to fetch public user profile: ${response.statusText}`);
      }

      const profile = await response.json();
      return { userId, profile };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Unknown error'
      );
    }
  }
);

interface UserState {
  profile: User | null;
  publicProfiles: Record<string, { id: string; username: string; name: string; sparkPoints: number; createdAt: string }>;
  streakSpark: StreakSparkData;
  preferences: {
    theme: 'light' | 'dark' | 'system';
    notifications: boolean;
    soundEnabled: boolean;
    calendarView: 'month' | 'week';
  };
  userPreferences: User['preferences'] | null;
  loading: boolean;
  error: string | null;
}

const getInitialPreferences = () => {
  const storedTheme = localStorage.getItem('streak-spark-theme') as 'light' | 'dark' | 'system' | null;
  return {
    theme: storedTheme || 'system',
    notifications: true,
    soundEnabled: true,
    calendarView: 'month' as const,
  };
};

const initialState: UserState = {
  profile: null,
  publicProfiles: {},
  streakSpark: {
    totalSparkPoints: 0,
    currentStreak: 0,
    longestStreak: 0,
    totalHabitsCompleted: 0,
    totalTodosCompleted: 0,
    achievements: [],
    level: 1,
    experiencePoints: 0,
  },
  preferences: getInitialPreferences(),
  userPreferences: null,
  loading: false,
  error: null,
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUserProfile: (state, action: PayloadAction<User>) => {
      state.profile = action.payload;
    },
    updateUserProfile: (state, action: PayloadAction<Partial<User>>) => {
      if (state.profile) {
        state.profile = { ...state.profile, ...action.payload };
      }
    },
    updateStreakSpark: (state, action: PayloadAction<Partial<StreakSparkData>>) => {
      state.streakSpark = { ...state.streakSpark, ...action.payload };
    },
    addSparkPoints: (state, action: PayloadAction<number>) => {
      state.streakSpark.totalSparkPoints += action.payload;
      state.streakSpark.experiencePoints += action.payload;

      // Level up logic (every 1000 XP = 1 level)
      const newLevel = Math.floor(state.streakSpark.experiencePoints / 1000) + 1;
      if (newLevel > state.streakSpark.level) {
        state.streakSpark.level = newLevel;
      }
    },
    incrementHabitCompletion: (state) => {
      state.streakSpark.totalHabitsCompleted += 1;
      state.streakSpark.currentStreak += 1;
      if (state.streakSpark.currentStreak > state.streakSpark.longestStreak) {
        state.streakSpark.longestStreak = state.streakSpark.currentStreak;
      }
    },
    incrementTodoCompletion: (state) => {
      state.streakSpark.totalTodosCompleted += 1;
    },
    resetCurrentStreak: (state) => {
      state.streakSpark.currentStreak = 0;
    },
    unlockAchievement: (state, action: PayloadAction<Achievement>) => {
      state.streakSpark.achievements.push({
        ...action.payload,
        unlockedAt: new Date().toISOString()
      });
    },
    updatePreferences: (state, action: PayloadAction<Partial<UserState['preferences']>>) => {
      state.preferences = { ...state.preferences, ...action.payload };
    },
    updateUserPreferences: (state, action: PayloadAction<Partial<User['preferences']>>) => {
      if (state.userPreferences) {
        state.userPreferences = { ...state.userPreferences, ...action.payload };
      }
    },
    logout: (state) => {
      state.profile = null;
      state.streakSpark = initialState.streakSpark;
      // Clear authentication data from localStorage
      clearAuthData();
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload;
        // Update streak spark with latest spark points
        state.streakSpark.totalSparkPoints = action.payload.sparkPoints;
        // Store user preferences
        state.userPreferences = action.payload.preferences;
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchPublicUserProfile.fulfilled, (state, action) => {
        const { userId, profile } = action.payload;
        state.publicProfiles[userId] = profile;
      });
  },
});

export const {
  setUserProfile,
  updateUserProfile,
  updateStreakSpark,
  addSparkPoints,
  incrementHabitCompletion,
  incrementTodoCompletion,
  resetCurrentStreak,
  unlockAchievement,
  updatePreferences,
  updateUserPreferences,
  logout,
  setLoading,
  setError,
} = userSlice.actions;

export { manualLogout, fetchUserProfile, fetchPublicUserProfile };

export default userSlice.reducer;
